import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Building2, Network, Clock, CheckCircle, Truck, Plus, ArrowRight, Wifi, Store, Briefcase, ChevronRight, ChevronDown, Search, Phone } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const statusBadge = (status) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    delivered: 'bg-sky-100 text-sky-700',
    approval_pending: 'bg-amber-100 text-amber-700',
  };
  return map[status] || 'bg-slate-100 text-slate-600';
};

const PartnerLcoTree = ({ API, navigate }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/dashboard/partner-lco-tree`)
      .then(res => setData(res.data))
      .catch(err => console.error('Failed to fetch partner-lco tree:', err));
  }, [API]);

  if (!data) return null;

  const q = search.trim().toLowerCase();
  const matches = (c) =>
    (c.company_name || '').toLowerCase().includes(q) ||
    (c.contact_name || '').toLowerCase().includes(q) ||
    (c.contact_phone || '').includes(q);

  const filteredTree = !q ? data.tree : data.tree
    .map(p => ({ ...p, lcos: p.lcos.filter(matches), selfMatch: matches(p) }))
    .filter(p => p.selfMatch || p.lcos.length > 0);
  const filteredUnassigned = !q ? (data.unassigned_lcos || []) : (data.unassigned_lcos || []).filter(matches);

  const isOpen = (id) => (q ? true : !!expanded[id]);

  const LcoRow = ({ lco }) => (
    <div
      className="flex items-center justify-between py-2 pl-10 pr-3 rounded-md hover:bg-slate-50 cursor-pointer border-l-2 border-slate-200 ml-4"
      onClick={() => navigate(`/customers/${lco.customer_id}`)}
      data-testid={`tree-lco-${lco.customer_id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Store className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />
        <span className="text-sm text-slate-700 truncate">{lco.company_name || lco.contact_name}</span>
        {lco.contact_phone && (
          <span className="text-xs text-slate-400 flex items-center gap-1 flex-shrink-0"><Phone className="h-3 w-3" />{lco.contact_phone}</span>
        )}
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${statusBadge(lco.workflow_status)}`}>
        {(lco.workflow_status || 'new').replace('_', ' ')}
      </span>
    </div>
  );

  return (
    <Card data-testid="partner-lco-tree-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" /> Partner → LCO Tree
            </CardTitle>
            <CardDescription>{data.partner_count} partners • {data.lco_count} LCOs</CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search partner or LCO..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="tree-search-input" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredTree.length === 0 && filteredUnassigned.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">No partners or LCOs {q ? 'match your search' : 'yet'}</p>
        ) : (
          <div className="space-y-1">
            {filteredTree.map(p => (
              <div key={p.customer_id}>
                <div
                  className="flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-slate-50 cursor-pointer"
                  onClick={() => setExpanded(prev => ({ ...prev, [p.customer_id]: !isOpen(p.customer_id) }))}
                  data-testid={`tree-partner-${p.customer_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isOpen(p.customer_id) ? <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                    <Briefcase className="h-4 w-4 text-sky-600 flex-shrink-0" />
                    <button
                      className="text-sm font-medium text-slate-900 hover:text-indigo-600 hover:underline truncate"
                      onClick={(e) => { e.stopPropagation(); navigate(`/customers/${p.customer_id}`); }}
                    >
                      {p.company_name}
                    </button>
                    {p.contact_phone && (
                      <span className="text-xs text-slate-400 hidden sm:flex items-center gap-1 flex-shrink-0"><Phone className="h-3 w-3" />{p.contact_phone}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="secondary" className="text-xs">{q ? p.lcos.length : p.lco_count} LCO{(q ? p.lcos.length : p.lco_count) === 1 ? '' : 's'}</Badge>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusBadge(p.workflow_status)}`}>
                      {(p.workflow_status || 'new').replace('_', ' ')}
                    </span>
                  </div>
                </div>
                {isOpen(p.customer_id) && (
                  p.lcos.length > 0
                    ? p.lcos.map(l => <LcoRow key={l.customer_id} lco={l} />)
                    : <p className="text-xs text-slate-400 pl-10 py-1.5 ml-4 border-l-2 border-slate-200">No LCOs under this partner</p>
                )}
              </div>
            ))}
            {filteredUnassigned.length > 0 && (
              <div className="pt-2 mt-2 border-t">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-1.5">Unassigned LCOs ({filteredUnassigned.length})</p>
                {filteredUnassigned.map(l => <LcoRow key={l.customer_id} lco={l} />)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function Dashboard() {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_customers: 0,
    partners: 0,
    lcos: 0,
    corporate: 0,
    bulk_bandwidth: 0,
    pending_approval: 0,
    pending_po: 0,
    pending_delivery: 0,
    total_circuits: 0,
    delivered_circuits: 0,
    recent_customers: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [API]);

  const statCards = [
    { title: 'Total Customers', value: stats.total_customers, icon: Users, color: 'bg-slate-600', link: '/customers' },
    { title: 'Partners', value: stats.partners, icon: Briefcase, color: 'bg-sky-500', link: '/customers?type=partner' },
    { title: 'LCOs', value: stats.lcos, icon: Store, color: 'bg-indigo-500', link: '/customers?type=lco' },
    { title: 'Corporate/Leased', value: stats.corporate, icon: Building2, color: 'bg-emerald-500', link: '/customers?type=corporate' },
    { title: 'Bulk Bandwidth', value: stats.bulk_bandwidth, icon: Wifi, color: 'bg-purple-500', link: '/customers?type=bulk_bandwidth' },
    { title: 'Pending Approval', value: stats.pending_approval, icon: Clock, color: 'bg-amber-500', highlight: true, link: '/customers?status=approval_pending' },
    { title: 'Pending PO', value: stats.pending_po, icon: Clock, color: 'bg-orange-500', highlight: true, link: '/customers?status=approved' },
    { title: 'Pending Delivery', value: stats.pending_delivery, icon: Truck, color: 'bg-violet-500', highlight: true, link: '/customers?status=delivery_pending' },
  ];

  // Helper function to check if user has any of the allowed roles
  const hasRole = (allowedRoles) => {
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    return allowedRoles.some(role => userRoles.includes(role));
  };

  const canCreate = hasRole(['admin', 'onboarding', 'sales', 'enterprise_sales', 'lco_sales']);
  const canApprove = hasRole(['admin']);
  const canRaisePO = hasRole(['admin', 'accounts']);
  const canDeliver = hasRole(['admin', 'delivery']);

  // Get all user roles for display
  const displayRoles = user?.roles?.length > 0 ? user.roles.join(', ') : user?.role;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-semibold text-slate-900 tracking-tight">Dashboard</h2>
          <p className="text-slate-600 mt-1">Welcome back, {user?.full_name} <span className="text-sky-600 capitalize">({displayRoles})</span></p>
        </div>
        {canCreate && (
          <Button 
            onClick={() => navigate('/customers/new')} 
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="new-customer-btn"
          >
            <Plus className="mr-2 h-4 w-4" /> New Customer
          </Button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.title}
              className={`${stat.highlight && stat.value > 0 ? 'ring-2 ring-amber-400' : ''} ${stat.link ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
              onClick={() => stat.link && navigate(stat.link)}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500">{stat.title}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {loading ? '...' : stat.value}
                    </p>
                  </div>
                  <div className={`${stat.color} p-2 rounded-lg`}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions by Role */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Based on your role: {user?.role}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {canCreate && (
              <>
                <Button 
                  variant="outline" 
                  className="w-full justify-between"
                  onClick={() => navigate('/customers/new')}
                >
                  <span className="flex items-center">
                    <Plus className="mr-2 h-4 w-4" />
                    Onboard New Customer
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-between"
                  onClick={() => navigate('/customers')}
                >
                  <span className="flex items-center">
                    <Users className="mr-2 h-4 w-4" />
                    View All Customers
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </>
            )}
            {canApprove && stats.pending_approval > 0 && (
              <Button 
                variant="outline" 
                className="w-full justify-between border-amber-300 bg-amber-50"
                onClick={() => navigate('/customers?status=approval_pending')}
              >
                <span className="flex items-center">
                  <Clock className="mr-2 h-4 w-4 text-amber-600" />
                  {stats.pending_approval} Pending Approvals
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
            {canRaisePO && stats.pending_po > 0 && (
              <Button 
                variant="outline" 
                className="w-full justify-between border-orange-300 bg-orange-50"
                onClick={() => navigate('/customers?status=approved')}
              >
                <span className="flex items-center">
                  <Building2 className="mr-2 h-4 w-4 text-orange-600" />
                  {stats.pending_po} Pending PO
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
            {canDeliver && stats.pending_delivery > 0 && (
              <Button 
                variant="outline" 
                className="w-full justify-between border-violet-300 bg-violet-50"
                onClick={() => navigate('/customers?status=delivery_pending')}
              >
                <span className="flex items-center">
                  <Truck className="mr-2 h-4 w-4 text-violet-600" />
                  {stats.pending_delivery} Pending Delivery
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Recent Customers */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Customers</CardTitle>
              <CardDescription>Latest onboarded customers</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/customers')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {stats.recent_customers.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">No customers yet</p>
            ) : (
              <div className="space-y-3">
                {stats.recent_customers.slice(0, 5).map((customer) => (
                  <div 
                    key={customer.customer_id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                    onClick={() => navigate(`/customers/${customer.customer_id}`)}
                  >
                    <div>
                      <p className="font-medium text-slate-900">
                        <a href={`/customers/${customer.customer_id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/customers/${customer.customer_id}`); }} className="text-indigo-600 hover:text-indigo-800 hover:underline">
                          {customer.company_name}
                        </a>
                      </p>
                      <p className="text-xs text-slate-500">
                        {customer.customer_code} • {customer.customer_type?.replace('_', ' ')}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      customer.workflow_status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      customer.workflow_status === 'delivered' ? 'bg-sky-100 text-sky-700' :
                      customer.workflow_status === 'approval_pending' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {customer.workflow_status?.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Partner → LCO Tree */}
      <PartnerLcoTree API={API} navigate={navigate} />

      {/* Circuit Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Network className="h-5 w-5" />
            Circuit Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-sm text-slate-600">Total Circuits</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total_circuits}</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg text-center">
              <p className="text-sm text-emerald-600">Delivered</p>
              <p className="text-2xl font-bold text-emerald-700">{stats.delivered_circuits}</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <p className="text-sm text-amber-600">Pending</p>
              <p className="text-2xl font-bold text-amber-700">{stats.total_circuits - stats.delivered_circuits}</p>
            </div>
            <div className="p-4 bg-sky-50 rounded-lg text-center">
              <p className="text-sm text-sky-600">Delivery Rate</p>
              <p className="text-2xl font-bold text-sky-700">
                {stats.total_circuits > 0 ? Math.round(stats.delivered_circuits / stats.total_circuits * 100) : 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
